import React, { ReactNode } from 'react';
import styled from '@emotion/styled';
import { Grid } from '@mui/material';

import { useAppSelector } from '@/redux/hooks';
import { PmamImage } from '@/redux/slices/slice_types';
import { selectAudioLanguages, selectSubtitlesLanguages } from '@/redux/selectors/constantsSelectors';
import { getAgeFromDate } from '@/_utils/date';
import { convertStylesToStyled } from '@/_utils/utils';
import { LabelValue } from '@/components/_common';
import { CSVLanguages } from '@/exports/columnCommonComponents';
import TableCollapse from '@/components/_common/Table/TableCollapse';
import PmamImages from '@/components/PendingAssets/PmamImages';

type PendingAssetRowExpandProps = {
  isCollapsed: boolean;
  item: any;
  children?: ReactNode;
};

const PendingAssetRowExpand = ({ isCollapsed, item = {}, children }: PendingAssetRowExpandProps) => {
  const AudioLanguages = useAppSelector(selectAudioLanguages);
  const SubtitleLanguages = useAppSelector(selectSubtitlesLanguages);
  const pmamImages: PmamImage[] | undefined = item?.info?.pmam_images;
  const hidePmamApply =
    !!item.material_id?.length ||
    !['unavailable', 'error', 'not_available'].includes(item.status.toLowerCase()) ||
    !item.component_type ||
    !['video', 'image', 'artwork'].includes(item.component_type.toLowerCase());

  return (
    <AssetRowExpand isCollapsed={isCollapsed}>
      <Grid container spacing={4}>
        <LabelValue label="Age">{getAgeFromDate(item.status_changed_at)}</LabelValue>
        <LabelValue label="Related Job(s)">
          {[...item.jobs]
            ?.sort((a: any, b: any) => a.id - b.id)
            .map((job: any, i: number) => [
              i > 0 && ', ',
              <a key={job.id} href={`/jobs/${job.id}`} style={{ textDecoration: 'none', color: 'var(--primary-main)' }}>
                {job.hid}
              </a>
            ])}
        </LabelValue>
        {!!item.requested_resolution && (
          <LabelValue label="Resolution">
            {item.requested_resolution ? (
              <span style={{ border: '1px solid #89898991', borderRadius: 3, padding: '2px 4px', fontSize: '.9rem' }}>
                {item.requested_resolution.toUpperCase()}
              </span>
            ) : (
              '-'
            )}
          </LabelValue>
        )}
        {item.info?.language ? (
          <LabelValue label="Language(s)">
            <CSVLanguages codes={[item.info.language]} type="all" />
          </LabelValue>
        ) : (
          <LabelValue label="Language(s)">
            {item.audio_language
              ? AudioLanguages?.find((l) => l.code.replace('-', '_') === item.audio_language.replace('-', '_'))?.label || item.audio_language
              : item.subtitle_language
                ? SubtitleLanguages?.find((l) => l.code.replace('-', '_') === item.subtitle_language.replace('-', '_'))?.label ||
                  item.subtitle_language
                : '-'}
          </LabelValue>
        )}
        {children}
      </Grid>
      {pmamImages && pmamImages.length > 0 && <PmamImages assetId={item.id} images={pmamImages} showApply={!hidePmamApply} />}
    </AssetRowExpand>
  );
};

export default PendingAssetRowExpand;

const AssetRowExpand = styled(TableCollapse)`
  padding: ${({ theme }) => theme.spacing(1, 2, 1, '35px')};

  & label {
    ${({ theme }) => convertStylesToStyled(theme.components?.MuiTableCell?.styleOverrides?.head)};
  }

  & .MuiGrid-item {
    margin-right: ${({ theme }) => theme.spacing(5)};
  }
`;
